import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import { getChartTitleOrFallback } from '@/components/chart/chart-helpers'
import type { ReportCommentItem } from '@/api/use-report-comments'
import type { SelectionContextItem, PageContextPayload } from './lib'
import {
  isAttachmentSelectionContextItem,
  isChartSelectionContextItem,
  isTextSelectionContextItem,
} from './selection-guards'

export type ChartContextItem = { chartId: string; chartTitle: string }

/** Bumped when add-to-chat actions auto-open chat so heavy report charts can drop to static preview (lighter layout transition). */
export type HeavyChartsContextBump = { nonce: number; reportId: string | null }

export interface ChatContextValue {
  reportId?: string | null
  reportName?: string | null
  reportPinned?: boolean
  isChatOpen?: boolean
  /** Chart contexts for display (set when user adds charts to chat). */
  chartContext?: ChartContextItem[]
  /** Request focus on chat input (set when addChartToChat opens chat). */
  shouldFocusInput?: boolean
  /** Page-level context for auto-injection (set by page hooks like useDashboardContext). */
  pageContext?: PageContextPayload | null
  /** Whether the user explicitly pinned the page context to chat (mirrors reportPinned). */
  pageContextPinned?: boolean
}

interface ChatContextState extends ChatContextValue {
  setContext: (next: ChatContextValue) => void
  /**
   * Drops heavy report charts to static preview before the chat panel opens.
   * Call before starting the sidebar open animation (or rely on `setContext` when
   * setting `isChatOpen: true`).
   */
  prepareHeavyChartsForChatOpen: (reportId?: string | null) => void
  /** Pending selections to add to chat (chart, attachment, or text range). Cleared after send. */
  pendingAddToChat: SelectionContextItem[] | null
  setPendingAddToChat: (selections: SelectionContextItem[] | null) => void
  /** Open chat and add the given chart selections. Appends to existing; skips duplicates. */
  addChartToChat: (selections: SelectionContextItem[]) => void
  /** Add a text selection to chat context. Opens chat and adds to pending selections. */
  addTextSelectionToChat: (selection: SelectionContextItem) => void
  /** Add an attachment selection to chat context. Opens chat and adds to pending selections. */
  addAttachmentToChat: (selection: SelectionContextItem) => void
  /** Remove chart(s) from context. If chartId provided, removes only that chart; else clears all. */
  clearChartContext: (chartId?: string) => void
  /**
   * One-shot user message to send from report text selection composer (after pendingAddToChat is set).
   * Cleared by ChatSidebar after a successful send.
   */
  pendingAutoSend: { id: number; text: string } | null
  clearPendingAutoSend: () => void
  /** Open chat, merge text selection into pending, queue immediate send (no sidebar input focus). */
  sendTextSelectionWithMessage: (selection: SelectionContextItem, message: string) => void
  /**
   * Increments when add-to-context auto-opens chat. Heavy report charts reset to static Plotly preview
   * for that report so the sidebar animation stays smooth.
   */
  heavyChartsContextBump: HeavyChartsContextBump
  /** Feedback items from the current report's comments (set by ChatSidebar). */
  reportFeedbackItems: ReportCommentItem[]
  setReportFeedbackItems: (items: ReportCommentItem[]) => void
  /** Callback to open the feedback panel and highlight a specific comment. */
  openFeedbackPanel: ((commentId: string) => void) | null
  setOpenFeedbackPanel: (fn: ((commentId: string) => void) | null) => void
}

const ChatContext = createContext<ChatContextState | null>(null)

export function ChatContextProvider({ children }: React.PropsWithChildren) {
  const [value, setValue] = useState<ChatContextValue>({})
  const [pendingAddToChat, setPendingAddToChat] = useState<SelectionContextItem[] | null>(null)
  const [pendingAutoSend, setPendingAutoSend] = useState<{ id: number; text: string } | null>(null)
  const [heavyChartsContextBump, setHeavyChartsContextBump] = useState<HeavyChartsContextBump>({
    nonce: 0,
    reportId: null,
  })
  const autoSendIdRef = useRef(0)
  const [reportFeedbackItems, setReportFeedbackItems] = useState<ReportCommentItem[]>([])
  const openFeedbackPanelRef = useRef<((commentId: string) => void) | null>(null)
  const [openFeedbackPanelVersion, setOpenFeedbackPanelVersion] = useState(0)

  const setOpenFeedbackPanel = useCallback((fn: ((commentId: string) => void) | null) => {
    openFeedbackPanelRef.current = fn
    setOpenFeedbackPanelVersion((v) => v + 1)
  }, [])

  const bumpHeavyChartsContext = useCallback((reportId: string | null | undefined) => {
    setHeavyChartsContextBump((b) => ({
      nonce: b.nonce + 1,
      reportId: reportId ?? null,
    }))
  }, [])

  const upsertPendingSelection = useCallback(
    (
      existing: SelectionContextItem[],
      candidate: SelectionContextItem,
      isDuplicate: (a: SelectionContextItem, b: SelectionContextItem) => boolean,
    ): SelectionContextItem[] => {
      const duplicateIndex = existing.findIndex((s) => isDuplicate(s, candidate))
      if (duplicateIndex >= 0) {
        const next = [...existing]
        next[duplicateIndex] = candidate
        return next
      }
      return [...existing, candidate]
    },
    [],
  )

  const getPinnedReportFields = useCallback(
    (prev: ChatContextValue, reportId: string, reportName?: string | null): Pick<
      ChatContextValue,
      'reportId' | 'reportName' | 'reportPinned'
    > => {
      return {
        reportId,
        reportName: reportName ?? prev.reportName,
        reportPinned: true,
      }
    },
    [],
  )

  const openChatWithPinnedReport = useCallback(
    (
      prev: ChatContextValue,
      reportId: string,
      reportName?: string | null,
      /**
       * When `false`, opens chat without focusing the sidebar input (e.g. auto-send from the
       * report text-selection composer).
       */
      focusInput: boolean = true,
    ): ChatContextValue => {
      return {
        ...prev,
        isChatOpen: true,
        shouldFocusInput: focusInput,
        ...getPinnedReportFields(prev, reportId, reportName),
      }
    },
    [getPinnedReportFields],
  )

  const openChatWithPinnedReportIfPresent = useCallback(
    (
      prev: ChatContextValue,
      reportId?: string,
      reportName?: string | null,
      /**
       * When `false`, opens chat without focusing the sidebar input (e.g. auto-send from the
       * report text-selection composer).
       */
      focusInput: boolean = true,
    ): ChatContextValue => {
      if (!reportId || reportId.startsWith('__')) {
        return {
          ...prev,
          isChatOpen: true,
          shouldFocusInput: focusInput,
        }
      }
      return openChatWithPinnedReport(prev, reportId, reportName, focusInput)
    },
    [openChatWithPinnedReport],
  )

  const upsertPendingSelections = useCallback(
    (
      incoming: SelectionContextItem[],
      isRelevant: (sel: SelectionContextItem) => boolean,
      isDuplicate: (a: SelectionContextItem, b: SelectionContextItem) => boolean,
    ) => {
      setPendingAddToChat((prev) => {
        const existing = prev ?? []
        let next = existing
        for (const sel of incoming) {
          if (!isRelevant(sel)) continue
          next = upsertPendingSelection(next, sel, isDuplicate)
        }
        return next
      })
    },
    [upsertPendingSelection],
  )

  const setContext = useCallback((next: ChatContextValue) => {
    let shouldPrepareHeavyCharts = false
    let reportIdForHeavyCharts: string | null | undefined

    setValue((prev) => {
      shouldPrepareHeavyCharts = next.isChatOpen === true && prev.isChatOpen !== true
      reportIdForHeavyCharts = next.reportId ?? prev.reportId

      const hasChanges =
        prev.reportId !== next.reportId ||
        prev.reportName !== next.reportName ||
        prev.reportPinned !== next.reportPinned ||
        prev.isChatOpen !== next.isChatOpen ||
        prev.chartContext !== next.chartContext ||
        prev.shouldFocusInput !== next.shouldFocusInput ||
        prev.pageContext !== next.pageContext ||
        prev.pageContextPinned !== next.pageContextPinned
      let result = hasChanges ? { ...prev, ...next } : prev
      // When report is explicitly cleared, clear all children (charts, pending selections).
      const reportCleared = 'reportId' in next && next.reportId === undefined
      if (reportCleared) {
        result = { ...result, chartContext: [] }
        setPendingAddToChat(null)
        setPendingAutoSend(null)
      }
      // When page context is cleared, clear pin.
      const pageContextCleared = 'pageContext' in next && next.pageContext == null
      if (pageContextCleared) {
        result = { ...result, pageContextPinned: undefined }
      }
      return result
    })

    if (shouldPrepareHeavyCharts) {
      bumpHeavyChartsContext(reportIdForHeavyCharts)
    }
  }, [bumpHeavyChartsContext])

  const addChartToChat = useCallback((selections: SelectionContextItem[]) => {
    const newCharts: ChartContextItem[] = []
    for (const sel of selections) {
      if (isChartSelectionContextItem(sel)) {
        newCharts.push({
          chartId: sel.selection.chart_id,
          chartTitle: getChartTitleOrFallback(sel.selection.chart_title),
        })
      }
    }
    if (newCharts.length === 0) return

    const first = selections[0]
    const reportIdFromSelection = first?.report_id
    const reportNameFromSelection = first?.report_title

    bumpHeavyChartsContext(reportIdFromSelection)

    setValue((prev) => {
      const existing = prev.chartContext ?? []
      const existingIds = new Set(existing.map((c) => c.chartId))
      const toAdd = newCharts.filter((c) => !existingIds.has(c.chartId))
      const chartContext =
        toAdd.length > 0 ? [...existing, ...toAdd] : existing

      const base = openChatWithPinnedReportIfPresent(prev, reportIdFromSelection, reportNameFromSelection)
      return { ...base, chartContext }
    })

    upsertPendingSelections(
      selections,
      isChartSelectionContextItem,
      (a, b) =>
        isChartSelectionContextItem(a) &&
        isChartSelectionContextItem(b) &&
        a.report_id === b.report_id &&
        a.section_id === b.section_id &&
        a.selection.chart_id === b.selection.chart_id,
    )
  }, [openChatWithPinnedReportIfPresent, upsertPendingSelections, bumpHeavyChartsContext])

  const addTextSelectionToChat = useCallback((selection: SelectionContextItem) => {
    if (!isTextSelectionContextItem(selection)) return

    bumpHeavyChartsContext(selection.report_id)

    setValue((prev) => openChatWithPinnedReport(prev, selection.report_id, selection.report_title))

    upsertPendingSelections(
      [selection],
      isTextSelectionContextItem,
      (a, b) =>
        isTextSelectionContextItem(a) &&
        isTextSelectionContextItem(b) &&
        a.report_id === b.report_id &&
        a.section_id === b.section_id &&
        a.selection.selection_start === b.selection.selection_start &&
        a.selection.selection_end === b.selection.selection_end,
    )
  }, [openChatWithPinnedReport, upsertPendingSelections, bumpHeavyChartsContext])

  const clearPendingAutoSend = useCallback(() => {
    setPendingAutoSend(null)
  }, [])

  const sendTextSelectionWithMessage = useCallback(
    (selection: SelectionContextItem, message: string) => {
      const trimmed = message.trim()
      if (!isTextSelectionContextItem(selection) || !trimmed) return

      bumpHeavyChartsContext(selection.report_id)

      setValue((prev) =>
        openChatWithPinnedReport(prev, selection.report_id, selection.report_title, false),
      )

      upsertPendingSelections(
        [selection],
        isTextSelectionContextItem,
        (a, b) =>
          isTextSelectionContextItem(a) &&
          isTextSelectionContextItem(b) &&
          a.report_id === b.report_id &&
          a.section_id === b.section_id &&
          a.selection.selection_start === b.selection.selection_start &&
          a.selection.selection_end === b.selection.selection_end,
      )

      autoSendIdRef.current += 1
      setPendingAutoSend({ id: autoSendIdRef.current, text: trimmed })
    },
    [openChatWithPinnedReport, upsertPendingSelections, bumpHeavyChartsContext],
  )

  const addAttachmentToChat = useCallback((selection: SelectionContextItem) => {
    if (!isAttachmentSelectionContextItem(selection)) return

    bumpHeavyChartsContext(selection.report_id)

    setValue((prev) => openChatWithPinnedReport(prev, selection.report_id, selection.report_title))

    upsertPendingSelections(
      [selection],
      isAttachmentSelectionContextItem,
      (a, b) =>
        isAttachmentSelectionContextItem(a) &&
        isAttachmentSelectionContextItem(b) &&
        a.report_id === b.report_id &&
        a.section_id === b.section_id &&
        a.selection.attachment_id === b.selection.attachment_id,
    )
  }, [openChatWithPinnedReport, upsertPendingSelections, bumpHeavyChartsContext])

  const clearChartContext = useCallback((chartId?: string) => {
    if (chartId != null) {
      setValue((prev) => {
        const existing = prev.chartContext ?? []
        const next = existing.filter((c) => c.chartId !== chartId)
        return { ...prev, chartContext: next }
      })
      setPendingAddToChat((prev) => {
        if (!prev) return null
        const next = prev.filter(
          (s) =>
            !('selection' in s && s.selection && 'chart_id' in s.selection) ||
            s.selection.chart_id !== chartId,
        )
        return next.length > 0 ? next : null
      })
    } else {
      setValue((prev) => ({ ...prev, chartContext: [] }))
      setPendingAddToChat(null)
      setPendingAutoSend(null)
    }
  }, [])

  const prepareHeavyChartsForChatOpen = bumpHeavyChartsContext

  const state = useMemo<ChatContextState>(
    () => ({
      ...value,
      setContext,
      prepareHeavyChartsForChatOpen,
      pendingAddToChat,
      setPendingAddToChat,
      addChartToChat,
      addTextSelectionToChat,
      addAttachmentToChat,
      clearChartContext,
      pendingAutoSend,
      clearPendingAutoSend,
      sendTextSelectionWithMessage,
      heavyChartsContextBump,
      reportFeedbackItems,
      setReportFeedbackItems,
      openFeedbackPanel: openFeedbackPanelRef.current,
      setOpenFeedbackPanel,
    }),
    [
      value,
      setContext,
      prepareHeavyChartsForChatOpen,
      pendingAddToChat,
      addChartToChat,
      addTextSelectionToChat,
      addAttachmentToChat,
      clearChartContext,
      pendingAutoSend,
      clearPendingAutoSend,
      sendTextSelectionWithMessage,
      heavyChartsContextBump,
      reportFeedbackItems,
      setOpenFeedbackPanel,
      openFeedbackPanelVersion,
    ],
  )

  return <ChatContext.Provider value={state}>{children}</ChatContext.Provider>
}

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error('useChatContext must be used within ChatContextProvider')
  }
  const {
    reportId, reportName, reportPinned, isChatOpen, chartContext, shouldFocusInput,
    pageContext, pageContextPinned,
  } = ctx
  return {
    reportId, reportName, reportPinned, isChatOpen, chartContext, shouldFocusInput,
    pageContext, pageContextPinned,
  }
}

export function useChatContextActions(): {
  setContext: (next: ChatContextValue) => void
  prepareHeavyChartsForChatOpen: (reportId?: string | null) => void
  addChartToChat: (selections: SelectionContextItem[]) => void
  addTextSelectionToChat: (selection: SelectionContextItem) => void
  addAttachmentToChat: (selection: SelectionContextItem) => void
  clearChartContext: (chartId?: string) => void
  sendTextSelectionWithMessage: (selection: SelectionContextItem, message: string) => void
} {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error('useChatContextActions must be used within ChatContextProvider')
  }
  return {
    setContext: ctx.setContext,
    prepareHeavyChartsForChatOpen: ctx.prepareHeavyChartsForChatOpen,
    addChartToChat: ctx.addChartToChat,
    addTextSelectionToChat: ctx.addTextSelectionToChat,
    addAttachmentToChat: ctx.addAttachmentToChat,
    clearChartContext: ctx.clearChartContext,
    sendTextSelectionWithMessage: ctx.sendTextSelectionWithMessage,
  }
}

/** For charts and highlights: read feedback items and open the feedback panel. */
export function useChatFeedbackPanel(): {
  reportFeedbackItems: ReportCommentItem[]
  openFeedbackPanel: ((commentId: string) => void) | null
} {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error('useChatFeedbackPanel must be used within ChatContextProvider')
  }
  return {
    reportFeedbackItems: ctx.reportFeedbackItems,
    openFeedbackPanel: ctx.openFeedbackPanel,
  }
}

/** For ChatSidebar: set feedback panel state for other components to consume. */
export function useChatFeedbackPanelActions(): {
  setReportFeedbackItems: (items: ReportCommentItem[]) => void
  setOpenFeedbackPanel: (fn: ((commentId: string) => void) | null) => void
} {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error('useChatFeedbackPanelActions must be used within ChatContextProvider')
  }
  return {
    setReportFeedbackItems: ctx.setReportFeedbackItems,
    setOpenFeedbackPanel: ctx.setOpenFeedbackPanel,
  }
}

/** For ChatSidebar: consume pending add-to-chat selections and clear after send. */
export function useChatPendingAddToChat(): {
  pendingAddToChat: SelectionContextItem[] | null
  setPendingAddToChat: (selections: SelectionContextItem[] | null) => void
  pendingAutoSend: { id: number; text: string } | null
  clearPendingAutoSend: () => void
} {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error('useChatPendingAddToChat must be used within ChatContextProvider')
  }
  return {
    pendingAddToChat: ctx.pendingAddToChat,
    setPendingAddToChat: ctx.setPendingAddToChat,
    pendingAutoSend: ctx.pendingAutoSend,
    clearPendingAutoSend: ctx.clearPendingAutoSend,
  }
}

export function useHeavyChartsContextBump(): HeavyChartsContextBump {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error('useHeavyChartsContextBump must be used within ChatContextProvider')
  }
  return ctx.heavyChartsContextBump
}

